"use client"

import AccessDenied from "@/components/custom-access-denied"
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FolderOutlined,
  LeftOutlined,
  ReloadOutlined,
  RightOutlined,
} from "@ant-design/icons"
import type { TableColumnsType, TableProps } from "antd"
import { Button, Checkbox, Image, Modal, Pagination, Space, Spin, Table, TreeSelect } from "antd"
import Title from "antd/es/typography/Title"
import React, { useMemo } from "react"
import { t } from "../../../../languages/i18n"
import { Formats, MediaData } from "../../../../lib/config/types"
import { formatDate, toImageSrc } from "../../../../lib/utils"
import { useMediaList, UseMediaListOptions } from "../hook/useMediaList"
import FileTypeIcon, { isImageFile, isVideoFile } from "../components/FileTypeIcon"
import MediaModalBodyEdit from "./MediaModalEditBody"
import {
  applyViewConfig,
  ConfigureViewButton,
  useViewConfig,
  type UseViewConfigResult,
} from "@/(admin)/components/view-config"
import { mediaColumnsRegistry } from "../columns-registry"

interface MediaListProps extends UseMediaListOptions {
  searchTerm?: string
  /** Modal picker: bật list-rows (thay grid) trong nhánh picker, không phải antd Table. */
  modalListView?: boolean
  /** Sort do dropdown ngoài điều khiển (modal picker + grid trang thư viện). Ưu tiên hơn "Configure view". */
  sortValue?: string
}

const MediaList: React.FC<MediaListProps> = ({
  searchTerm,
  folderId,
  isView = true,
  isOpenModal,
  onSelectMedia,
  selectMode = "single",
  onBulkActionComplete,
  modalListView = false,
  sortValue,
}) => {
  const vc = useViewConfig("media", mediaColumnsRegistry, { pageSize: 20 })
  // Dropdown sort (modal picker + grid trang thư viện) → ưu tiên; nếu không có thì
  // dùng sort mặc định của "Configure view".
  const sort =
    sortValue ??
    (vc.config.sortField ? `${vc.config.sortField}:${vc.config.sortOrder ?? "desc"}` : undefined)

  const vm = useMediaList({
    searchTerm,
    folderId,
    isView,
    isOpenModal,
    selectMode,
    onSelectMedia,
    onBulkActionComplete,
    viewPageSize: vc.config.pageSize,
    sort,
  })

  if (!vm.checkPermission("media", "find")) {
    return <AccessDenied />
  }

  return (
    <TableOrGrid vm={vm} isView={isView} isOpenModal={isOpenModal} vc={vc} modalListView={modalListView} />
  )
}

export default MediaList

function TableOrGrid({
  vm,
  isView,
  isOpenModal,
  vc,
  modalListView,
}: {
  vm: ReturnType<typeof useMediaList>
  isView: boolean
  isOpenModal?: boolean
  vc: UseViewConfigResult
  modalListView?: boolean
}) {
  const viewConfig = vc.config
  const columns: TableColumnsType<MediaData> = useMemo(
    () => [
      {
        title: (
          <Checkbox
            checked={vm.allBulkSelected}
            indeterminate={vm.someBulkSelected}
            onChange={e => vm.toggleBulkAll(e.target.checked)}
          />
        ),
        key: "bulk",
        width: 48,
        render: (_: unknown, record: MediaData) => (
          <Checkbox
            checked={vm.bulkSelected.includes(record.id)}
            onChange={() => vm.toggleBulk(record.id)}
            onClick={e => e.stopPropagation()}
          />
        ),
      },
      {
        title: t("Thumbnail"),
        dataIndex: ["formats"],
        key: "url",
        width: 96,
        render: (formats: Formats, record: MediaData) =>
          isVideoFile(record) ? (
            <video
              src={toImageSrc(record.url)}
              style={{ objectFit: "contain" }}
              controls={false}
              autoPlay={false}
              muted
              className="h-[52px] w-[52px] rounded-md bg-black object-contain"
            />
          ) : isImageFile(record) ? (
            <div className="flex h-[52px] w-[52px] items-center justify-center overflow-hidden rounded-md bg-[#f5f5f5]">
              <Image
                src={toImageSrc(formats?.thumbnail?.url || record.url)}
                height={52}
                width={52}
                className="object-contain cursor-pointer"
                preview={{ src: toImageSrc(record.url), mask: <EyeOutlined style={{ fontSize: 16 }} /> }}
              />
            </div>
          ) : (
            // File tài liệu (pdf/word/excel/…): hiện icon theo loại thay vì <Image> vỡ.
            <a
              href={toImageSrc(record.url)}
              target="_blank"
              rel="noopener noreferrer"
              title={record.name}
              className="flex h-[52px] w-[52px] items-center justify-center rounded-md bg-[#f5f5f5]"
              onClick={e => e.stopPropagation()}
            >
              <FileTypeIcon mime={record.mime} name={record.name} fontSize={30} />
            </a>
          ),
      },
      {
        title: t("Name"),
        dataIndex: ["name"],
        key: "name",
        width: "28%",
        sorter: true,
        showSorterTooltip: false,
      },
      {
        title: t("Published"),
        dataIndex: ["createdAt"],
        key: "createdAt",
        width: "14%",
        render: (createdAt: string) => {
          const d = formatDate(createdAt)
          return (
            <span className="font-normal">
              <span className="text-black-600">{d.formattedTime}</span> {d.formattedDate}
            </span>
          )
        },
        sorter: true,
        showSorterTooltip: false,
      },
      {
        title: t("Last Updated"),
        dataIndex: ["updatedAt"],
        key: "updatedAt",
        width: "14%",
        render: (updatedAt: string) => {
          const d = formatDate(updatedAt)
          return (
            <span className="font-normal">
              <span className="text-black-600">{d.formattedTime}</span> {d.formattedDate}
            </span>
          )
        },
        sorter: true,
        showSorterTooltip: false,
      },
      {
        title: t("Action"),
        key: "action",
        width: "16%",
        render: (_: unknown, record: MediaData, index: number) => (
          <Space size="middle" className="justify-center items-center">
            {vm.checkPermission("media", "update") && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                className="bg-blue-500 px-4 py-2 rounded"
                onClick={() => vm.setSelectedMedia(index)}
              >
                {t("Edit")}
              </Button>
            )}
            {vm.checkPermission("media", "delete") && (
              <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => vm.handleDelete(record.id)}>
                <span>{t("Delete")}</span>
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [vm.checkPermission, vm.bulkSelected, vm.allBulkSelected, vm.someBulkSelected, vm.dataItem],
  )

  // Split off the leading bulk-checkbox column so it stays first and is gated by
  // the "bulk" toggle; the rest are filtered/reordered per the saved view config.
  const displayColumns = useMemo(() => {
    const [bulkCol, ...dataCols] = columns
    const applied = applyViewConfig(dataCols, mediaColumnsRegistry, viewConfig)
    return viewConfig.enableBulk ? [bulkCol, ...applied] : applied
  }, [columns, viewConfig])

  return (
    <div
      style={{
        height: isOpenModal ? "650px" : "auto",
        overflowY: isOpenModal ? "scroll" : "visible",
        marginTop: 20,
        position: "relative",
      }}
      key={isView ? "table-mode" : "grid-mode"}
    >
      {vm.contextHolder}

      {!isOpenModal && (
        <div className="flex justify-end gap-2 mb-3">
          <Button icon={<ReloadOutlined />} onClick={() => vm.refreshData()}>
            {t("Refresh")}
          </Button>
          <ConfigureViewButton
            registry={mediaColumnsRegistry}
            config={vc.config}
            defaultConfig={vc.defaultConfig}
            setConfig={vc.setConfig}
            reset={vc.reset}
            features={{ search: false, filters: false, bulk: true }}
          />
        </div>
      )}

      {viewConfig.enableBulk && vm.bulkSelected.length > 0 && (vm.canBulkDelete || vm.canBulkUpdate) && (
        <div className="flex flex-wrap items-center gap-3 mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg sticky top-0 z-10">
          <span className="text-sm text-blue-700 font-medium">
            {t("Selected")} {vm.bulkSelected.length} {t("items")}
          </span>
          {vm.canBulkUpdate && (
            <Button
              type="primary"
              size="large"
              icon={<FolderOutlined />}
              className="bg-[#3B82F6]"
              onClick={vm.openBulkMoveModal}
            >
              {t("Di chuyển")}
            </Button>
          )}
          {vm.canBulkDelete && (
            <Button danger size="large" icon={<DeleteOutlined />} onClick={vm.handleBulkDelete}>
              {t("Xóa tệp")}
            </Button>
          )}
          <Button size="large" onClick={vm.clearBulkSelection}>
            {t("Cancel")}
          </Button>
        </div>
      )}

      <Modal
        title={
          <div className="flex items-center gap-2 py-1">
            <span className="text-lg font-semibold text-gray-800">{t("Move")}</span>
          </div>
        }
        open={vm.moveModalOpen}
        onOk={vm.handleConfirmBulkMove}
        onCancel={vm.closeBulkMoveModal}
        confirmLoading={vm.moving}
        okText={t("Move")}
        cancelText={t("Cancel")}
        okButtonProps={{
          disabled: vm.moveTargetFolderId == null,
          size: "large",
          className: "min-w-[100px] rounded-lg shadow-sm",
        }}
        cancelButtonProps={{
          size: "large",
          className: "min-w-[100px] rounded-lg",
        }}
        centered
        destroyOnHidden
        width={520}
        bodyStyle={{ paddingTop: "12px", paddingBottom: "8px" }}
      >
        <Spin spinning={vm.foldersMoveLoading}>
          <div className="flex flex-col gap-3">
            <div className="bg-blue-50 border border-blue-100 rounded-md p-3">
              <p className="text-sm text-blue-700 m-0">{t("Select destination folder")}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500 ml-1 uppercase tracking-wider">
                {t("Target folder")}
              </label>
              <TreeSelect
                className="w-full custom-tree-select"
                size="large"
                treeData={vm.moveTreeData}
                placeholder={t("Select folder")}
                value={vm.moveTargetFolderId ?? undefined}
                onChange={v => {
                  vm.setMoveTargetFolderId(v == null ? null : Number(v))
                }}
                showSearch
                treeLine={{ showLeafIcon: false }}
                allowClear
                treeDefaultExpandAll
                dropdownStyle={{ maxHeight: 400, overflow: "auto", borderRadius: "8px" }}
                filterTreeNode={(input, node) =>
                  String(node.title ?? "")
                    .toLowerCase()
                    .includes(input.trim().toLowerCase())
                }
                style={{ borderRadius: "8px" }}
              />
            </div>
          </div>
        </Spin>
      </Modal>

      {vm.selectMode === "multi" && !isView && (
        <div className="flex justify-end mb-4 sticky top-0 bg-white z-10">
          <Button
            type="primary"
            disabled={vm.selectedMediaMulti.length === 0}
            onClick={vm.confirmMultiSelect}
          >
            {t("Select")} {vm.selectedMediaMulti.length ? `${vm.selectedMediaMulti.length} ${t("image")}` : ""}
          </Button>
        </div>
      )}

      {vm.selectMode === "single" && isOpenModal && vm.pendingMedia && !isView && (
        <div className="z-[20] flex items-center justify-between mb-4 px-3 py-2 sticky top-0 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm text-blue-700 truncate max-w-[60%]">
            {t("Selected")}: <strong>{vm.pendingMedia.name}</strong>
          </span>
          <Space>
            <Button onClick={vm.cancelPendingMedia}>{t("Cancel")}</Button>
            <Button type="primary" onClick={vm.confirmPendingMedia}>
              {t("OK")}
            </Button>
          </Space>
        </div>
      )}

      {isView ? (
        <>
          <Table
            className="mt-3 border border-gray-300 custom-table overflow-hidden"
            rowKey="id"
            columns={displayColumns}
            loading={vm.loading}
            dataSource={vm.table.data}
            onChange={((pg, filters, sorter, extra) => vm.handleTableChange(pg, filters, sorter, extra)) as TableProps<MediaData>["onChange"]}
            pagination={{
              current: vm.table.pagination.current,
              pageSize: vm.table.pagination.pageSize || viewConfig.pageSize,
              total: vm.table.pagination.total,
            }}
            scroll={{ x: "100%", y: 600 }}
            style={{ border: "1px solid #e8e8e8" }}
            rowClassName={(_, index) => (index % 2 === 0 ? "bg-[#f6f7f7]" : "bg-white")}
          />

          {vm.selectedMedia != null && vm.dataItem[vm.selectedMedia] && (
            <Modal
              open={vm.selectedMedia != null}
              style={{ top: 65 }}
              width="95%"
              closable={false}
              footer={null}
              onCancel={vm.closeEditModal}
              title={
                <div className="flex justify-between text-center">
                  <Title level={2} className="m-0">
                    {t("Attachment details")}
                  </Title>
                  <Space>
                    <Button type="text" onClick={() => vm.navigateEditMedia("prev")}>
                      <LeftOutlined />
                    </Button>
                    <Button type="text" onClick={() => vm.navigateEditMedia("next")}>
                      <RightOutlined />
                    </Button>
                    <Button type="text" onClick={vm.closeEditModal} className="close-icon">
                      <CloseOutlined />
                    </Button>
                  </Space>
                </div>
              }
            >
              <MediaModalBodyEdit
                media={vm.dataItem[vm.selectedMedia]}
                loading={vm.loading}
                handleFormSubmit={vm.handleFormSubmit}
                refreshMediaList={() => vm.table.fetchPage({ ...vm.table.pagination })}
                setIsView={() => {}}
              />
            </Modal>
          )}
        </>
      ) : (
        <>
          {vm.dataItem.length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              {/* Multi picker: "Chọn tất cả" đổ vào tập CHÈN (selectedMediaMulti);
                  ngoài ra vẫn là tập bulk xoá/di chuyển. */}
              <Checkbox
                checked={vm.selectMode === "multi" ? vm.allMultiSelected : vm.allBulkSelected}
                indeterminate={vm.selectMode === "multi" ? vm.someMultiSelected : vm.someBulkSelected}
                onChange={e =>
                  vm.selectMode === "multi"
                    ? vm.toggleSelectAllMulti(e.target.checked)
                    : vm.toggleBulkAll(e.target.checked)
                }
              />
              <span className="text-xs text-[#666687]">{t("Select all")}</span>
            </div>
          )}

          {vm.loading ? (
            <div className="flex justify-center items-center h-40">
              <Spin />
            </div>
          ) : modalListView ? (
            // List-rows cho modal picker: hiện tên + meta rõ ràng (giống modal cũ),
            // dùng chung logic chọn với grid.
            <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-4 px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 sticky top-0 z-10">
                <div className="w-5 shrink-0" />
                <div className="w-12 shrink-0" />
                <div className="flex-1 min-w-0">{t("Name")}</div>
                <div className="w-20 shrink-0 text-center">{t("Định dạng")}</div>
                <div className="w-24 shrink-0 text-center">{t("Kích thước ảnh")}</div>
                <div className="w-20 shrink-0 text-right">{t("Dung lượng")}</div>
                <div className="w-24 shrink-0 text-right">{t("Published")}</div>
              </div>
              {vm.dataItem.map(media => {
                const isPicked = vm.selectedMediaMulti.some(m => m.id === media.id)
                const isBulkChecked = vm.bulkSelected.includes(media.id)
                const isPending = vm.pendingMedia?.id === media.id
                const isMulti = vm.selectMode === "multi"
                return (
                  <ListRow
                    key={media.id}
                    media={media}
                    checked={isMulti ? isPicked : isBulkChecked}
                    highlighted={isPending || isPicked}
                    onToggleCheck={() =>
                      isMulti ? vm.handleSelectMultiMedia(media) : vm.toggleBulk(media.id)
                    }
                    onClickRow={() => {
                      if (vm.selectMode === "multi") vm.handleSelectMultiMedia(media)
                      else vm.handleSelectMedia(media)
                    }}
                  />
                )
              })}
            </div>
          ) : (
            // Cố định 5 cột ở màn admin (≥lg) để ảnh to & nét, và chia hết page size
            // 20/50/100 → luôn đủ hàng. Sub-threshold (màn nhỏ) mới co còn 2–3 cột.
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {vm.dataItem.map((media, index) => {
                const isPicked = vm.selectedMediaMulti.some(m => m.id === media.id)
                const isBulkChecked = vm.bulkSelected.includes(media.id)
                const isPending = vm.pendingMedia?.id === media.id
                const isMulti = vm.selectMode === "multi"

                return (
                  <GridCard
                    key={media.id}
                    media={media}
                    index={index}
                    isPicked={isPicked}
                    isPending={isPending}
                    // Multi: checkbox góc = chọn-để-chèn (selectedMediaMulti) để đồng
                    // bộ với "Chọn tất cả"; ngoài multi giữ nguyên là bulk xoá/move.
                    isBulkChecked={isMulti ? isPicked : isBulkChecked}
                    isOpenModal={isOpenModal}
                    selectMode={vm.selectMode}
                    selectedMedia={vm.selectedMedia}
                    loading={vm.loading}
                    onToggleBulk={
                      isMulti ? () => vm.handleSelectMultiMedia(media) : vm.toggleBulk
                    }
                    onSelectMedia={() => {
                      if (isOpenModal) {
                        if (vm.selectMode === "multi") vm.handleSelectMultiMedia(media)
                        else vm.handleSelectMedia(media)
                      } else {
                        vm.setSelectedMedia(index)
                      }
                    }}
                    onFormSubmit={vm.handleFormSubmit}
                    onNavigate={vm.navigateEditMedia}
                    onCloseModal={vm.closeEditModal}
                    onRefreshGrid={vm.refreshData}
                  />
                )
              })}
            </div>
          )}

          {/* Phân trang thật (thay nút "Xem thêm" infinite). */}
          <div className="flex justify-end mt-5">
            <Pagination
              current={vm.table.pagination.current}
              pageSize={vm.table.pagination.pageSize}
              total={vm.table.pagination.total}
              onChange={(p, ps) =>
                vm.table.fetchPage({ current: p, pageSize: ps, total: vm.table.pagination.total })
              }
              showSizeChanger
              showTotal={tot => `${tot} tệp`}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface GridCardProps {
  media: MediaData
  index: number
  isPicked: boolean
  isPending: boolean
  isBulkChecked: boolean
  isOpenModal?: boolean
  selectMode: "single" | "multi"
  selectedMedia: number | null
  loading: boolean
  onToggleBulk: (id: number) => void
  onSelectMedia: () => void
  onFormSubmit: (
    value: { title: string; alternativeText: string; caption: string; width?: number; height?: number },
    file?: File,
  ) => Promise<void>
  onNavigate: (direction: "prev" | "next") => void
  onCloseModal: () => void
  onRefreshGrid: () => void
}

function GridCard({
  media,
  index,
  isPicked,
  isPending,
  isBulkChecked,
  selectedMedia,
  loading,
  onToggleBulk,
  onSelectMedia,
  onFormSubmit,
  onNavigate,
  onCloseModal,
  onRefreshGrid,
}: GridCardProps) {
  const borderClass = isPending
    ? "border-green-500 ring-2 ring-green-300"
    : isBulkChecked || isPicked
      ? "border-blue-500 ring-2 ring-blue-300"
      : "border-gray-200"

  const imgSrc = toImageSrc(
    media.url ||
      media?.formats?.large?.url ||
      media?.formats?.medium?.url ||
      media?.formats?.small?.url ||
      media?.formats?.thumbnail?.url,
  )

  return (
    <div
      className={`group relative flex flex-col overflow-hidden rounded-lg border bg-white transition-all duration-100 ${borderClass}`}
    >
      {/* Vùng preview: nền caro (giống Strapi) để ảnh trong suốt/mọi tỉ lệ đều rõ,
          ảnh object-contain nên không tràn che checkbox. */}
      <div
        className="relative flex aspect-square cursor-pointer items-center justify-center overflow-hidden bg-[#f4f5f7]"
        onClick={onSelectMedia}
      >
        <div className="absolute top-1 left-1 z-10" onClick={e => e.stopPropagation()}>
          <Checkbox checked={isBulkChecked} onChange={() => onToggleBulk(media.id)} />
        </div>

        {isVideoFile(media) ? (
          <video
            src={toImageSrc(media.url)}
            controls={false}
            muted
            className="max-h-full max-w-full object-contain"
            style={{ pointerEvents: "none" }}
          />
        ) : isImageFile(media) ? (
          <img src={imgSrc} alt={media.name} loading="lazy" className="max-h-full max-w-full object-contain" />
        ) : (
          <FileTypeIcon mime={media.mime} name={media.name} fontSize={48} />
        )}
      </div>

      {/* Caption: tên + loại + kích thước (giống Strapi). */}
      <div className="border-t border-gray-100 px-2 py-1.5">
        <div className="truncate text-xs font-medium text-gray-700" title={media.name}>
          {media.name}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] uppercase text-gray-400">
          <span>{(media.ext || "").replace(".", "") || media.mime?.split("/")[1] || "—"}</span>
          {media.width && media.height ? <span>· {media.width}×{media.height}</span> : null}
        </div>
      </div>

      <Modal
        open={selectedMedia === index}
        style={{ top: 65 }}
        width="95%"
        closable={false}
        footer={null}
        title={
          <div className="flex justify-between text-center">
            <Title level={2} className="m-0">
              {t("Attachment details")}
            </Title>
            <Space>
              <Button type="text" onClick={() => onNavigate("prev")}>
                <LeftOutlined />
              </Button>
              <Button type="text" onClick={() => onNavigate("next")}>
                <RightOutlined />
              </Button>
              <Button type="text" onClick={onCloseModal} className="close-icon">
                <CloseOutlined />
              </Button>
            </Space>
          </div>
        }
      >
        <MediaModalBodyEdit
          media={media}
          loading={loading}
          handleFormSubmit={onFormSubmit}
          refreshMediaList={onRefreshGrid}
          setIsView={() => {}}
        />
      </Modal>
    </div>
  )
}

function formatBytes(bytes?: number) {
  if (!bytes) return "—"
  // Strapi trả size theo KB (số thực), khác media.size raw (byte). Ở đây media.size
  // đã là KB (theo type Strapi) → hiển thị KB/MB cho gọn.
  if (bytes < 1024) return `${bytes.toFixed(bytes < 10 ? 1 : 0)} KB`
  return `${(bytes / 1024).toFixed(1)} MB`
}

function ListRow({
  media,
  checked,
  highlighted,
  onToggleCheck,
  onClickRow,
}: {
  media: MediaData
  checked: boolean
  highlighted: boolean
  onToggleCheck: () => void
  onClickRow: () => void
}) {
  const d = formatDate(media.createdAt)
  return (
    <div
      className={`flex items-center gap-4 px-3 py-2 cursor-pointer transition-colors ${
        highlighted ? "bg-blue-50" : "bg-white hover:bg-gray-50"
      }`}
      onClick={onClickRow}
    >
      <div className="w-5 shrink-0" onClick={e => e.stopPropagation()}>
        <Checkbox checked={checked} onChange={onToggleCheck} />
      </div>
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-[#f4f5f7]">
        {isVideoFile(media) ? (
          <video
            src={toImageSrc(media.url)}
            muted
            className="h-full w-full object-contain"
            style={{ pointerEvents: "none" }}
          />
        ) : isImageFile(media) ? (
          <img
            src={toImageSrc(media?.formats?.thumbnail?.url || media.url)}
            alt={media.name}
            loading="lazy"
            className="h-full w-full object-contain"
          />
        ) : (
          <FileTypeIcon mime={media.mime} name={media.name} fontSize={24} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm font-medium text-gray-700" title={media.name}>
          {media.name}
        </div>
      </div>
      <div className="w-20 shrink-0 text-center text-xs uppercase text-gray-500">
        {(media.ext || "").replace(".", "") || media.mime?.split("/")[1] || "—"}
      </div>
      <div className="w-24 shrink-0 text-center text-xs text-gray-400">
        {media.width && media.height ? `${media.width} × ${media.height}` : "—"}
      </div>
      <div className="w-20 shrink-0 text-right text-xs text-gray-500">{formatBytes(media.size)}</div>
      <div className="w-24 shrink-0 text-right text-xs text-gray-400">{d.formattedDate}</div>
    </div>
  )
}
